import {
  createContext,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useReducer,
} from "react";
import { Api } from "services/api/api.service";
import { useCompanyStore } from "../companyStore/companyStore";
import {
  OrganizationalStructureActions,
  organizationalStructureInitState,
  OrganizationalStructureState,
  organizationalStructureStateReducer,
} from "./organizationalStructureStore.reducers";

interface OrganizationalStructureStoreProviderProps {
  readonly children?: ReactNode;
}

export type OrganizationalStructureDispatch = (
  action: OrganizationalStructureActions,
) => void;

type UseOrganizationalStructureStoreResult = readonly [
  OrganizationalStructureState,
  OrganizationalStructureDispatch,
];

const OrganizationalStructureStateContext = createContext<
  OrganizationalStructureState | undefined
>(undefined);
const OrganizationalStructureDispatchContext = createContext<
  OrganizationalStructureDispatch | undefined
>(undefined);

export const OrganizationalStructureStoreProvider = (
  props: OrganizationalStructureStoreProviderProps,
): ReactElement => {
  const [state, dispatch] = useReducer(
    organizationalStructureStateReducer,
    organizationalStructureInitState,
  );
  const [companyState] = useCompanyStore();

  useEffect(() => {
    const companyId = companyState.company?.id;
    let isCurrentRequest = true;
    dispatch({ type: "GET_COMPANY_STRUCTURE", payload: { entities: [] } });

    const loadStructure = async (): Promise<void> => {
      if (!companyId) return;
      try {
        const response =
          await Api().company.organizationalStructure.fetchCompanyStructure(
            companyId,
          );
        if (isCurrentRequest) {
          dispatch({
            type: "GET_COMPANY_STRUCTURE",
            payload: response.data,
          });
        }
      } catch (error) {
        if (!isCurrentRequest) return;
        const requestError =
          error instanceof Error
            ? error
            : new Error("Failed to load organizational structure");
        dispatch({
          type: "SET_REQUEST_ERROR",
          payload: { message: requestError.message, err: requestError },
        });
      }
    };

    void loadStructure();
    return () => {
      isCurrentRequest = false;
    };
  }, [companyState.company?.id]);

  return (
    <OrganizationalStructureStateContext.Provider value={state}>
      <OrganizationalStructureDispatchContext.Provider value={dispatch}>
        {props.children}
      </OrganizationalStructureDispatchContext.Provider>
    </OrganizationalStructureStateContext.Provider>
  );
};

export const useOrganizationalStructureStore =
  (): UseOrganizationalStructureStoreResult => {
    const state = useContext(OrganizationalStructureStateContext);
    const dispatch = useContext(OrganizationalStructureDispatchContext);

    if (state === undefined || dispatch === undefined) {
      throw new Error(
        "useOrgStructureStore must be used within a OrganizationalStructureStoreProvider",
      );
    }

    return [state, dispatch];
  };
